import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PageContainer from '../components/PageContainer';
import TimeRangeSelector, { ranges } from '../components/TimeRangeSelector';
import FeedToPlantChart from '../components/FeedToPlantChart';
import InspectionHistoryTable from '../components/InspectionHistoryTable';
import EquipmentStatusTable from '../components/EquipmentStatusTable';
import DowntimeChart from '../components/DowntimeChart';
import { formatDateForApi } from '../utils/date';
import useAutoRefresh from '../hooks/useAutoRefresh';

const panelClasses =
  'flex flex-col min-h-0 overflow-hidden rounded-3xl border border-white/10 bg-white/70 p-4 sm:p-5 text-slate-900 shadow-xl shadow-slate-900/10 backdrop-blur-xl dark:border-white/5 dark:bg-slate-900/60 dark:text-white dark:shadow-black/20';

export default function EquipmentStatus() {
  const [range, setRange] = useState(ranges[1]);
  const [data, setData] = useState([]);
  const [compactLayout, setCompactLayout] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 1400 : false
  );

  // layout refs/state
  const wrapperRef = useRef(null);   // wraps header + content and has the vertical gap
  const headerRef = useRef(null);
  const [headerH, setHeaderH] = useState(0);
  const [wrapperGapY, setWrapperGapY] = useState(24); // default ~gap-6
  const [vh, setVh] = useState(typeof window !== 'undefined' ? window.innerHeight : 800);

  // 1) Fetch data
  const fetchData = useCallback(async () => {
    const end = new Date();
    const start = new Date(end);
    if (range.unit === 'hour') {
      start.setHours(end.getHours() - range.value);
    } else {
      start.setDate(end.getDate() - range.value);
    }
    try {
      const res = await fetch(
        `/api/equipment_status.php?start=${formatDateForApi(start)}&end=${formatDateForApi(end)}&_=${Date.now()}`
      );
      const json = await res.json();
      json.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setData(json);
    } catch (e) {
      console.error(e);
    }
  }, [range]);

  useAutoRefresh(fetchData, [range]);

  // 2) Keep viewport height, header height, and wrapper gap in sync
  useLayoutEffect(() => {
    const onResize = () => {
      setVh(window.innerHeight);
      setCompactLayout(window.innerWidth <= 1400);
      // measure wrapper row-gap (between header and content)
      if (wrapperRef.current) {
        const cs = window.getComputedStyle(wrapperRef.current);
        const rg = parseFloat(cs.rowGap || '0');
        setWrapperGapY(Number.isFinite(rg) ? rg : 0);
      }
    };
    window.addEventListener('resize', onResize);

    // initial measure
    onResize();
    if (headerRef.current) {
      const h = Math.ceil(headerRef.current.getBoundingClientRect().height);
      setHeaderH(h);
    }

    // observe header changes
    let ro;
    if (typeof ResizeObserver !== 'undefined' && headerRef.current) {
      ro = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setHeaderH(Math.ceil(entry.contentRect.height));
        }
      });
      ro.observe(headerRef.current);
    }

    return () => {
      window.removeEventListener('resize', onResize);
      if (ro && headerRef.current) ro.unobserve(headerRef.current);
    };
  }, []);

  // 3) Calculate available content height (never negative)
  const contentHeight = Math.max(200, vh - headerH - wrapperGapY);
  // Two equal rows inside the content grid
  const gapY = 24; // internal grid gap between the two rows (gap-6)
  const rowHeight = Math.max(220, Math.floor((contentHeight - gapY) / 2)); // floor 220 to keep charts visible

  const summary = useMemo(() => {
    const totalEntries = data.length;
    const equipmentSet = new Set();
    let downtimeEvents = 0;

    data.forEach((item) => {
      const equipmentId = item.equipment_id || item.equipment || item.asset || item.asset_id;
      if (equipmentId) equipmentSet.add(equipmentId);
      const status = (item.status || item.current_status || '').toString().toLowerCase();
      if (status.includes('down') || status.includes('maintenance')) downtimeEvents += 1;
    });

    return {
      totalEntries,
      equipmentCount: equipmentSet.size,
      downtimeEvents,
    };
  }, [data]);

  return (
    <PageContainer className="relative h-screen overflow-hidden bg-slate-950 text-slate-100">
      {/* Soft gradients */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(56,189,248,0.18),transparent_55%),radial-gradient(circle_at_bottom_right,rgba(16,185,129,0.16),transparent_55%)]" />

      {/* WRAPPER (has vertical gap we measure) */}
      <div
        ref={wrapperRef}
        className="relative z-10 flex h-full min-h-0 flex-col gap-5 overflow-hidden md:gap-8"
      >
        {/* HEADER (auto height, measured) */}
        <header ref={headerRef} className="flex flex-shrink-0 flex-col gap-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="space-y-2">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-200">
                Reliability Command
              </span>
              <div>
                <h1 className="text-2xl font-semibold leading-tight text-white sm:text-3xl md:text-4xl">Equipment Status</h1>
                <p className="mt-2 max-w-2xl text-sm text-slate-300 md:text-base">
                  Live condition monitoring, inspection cadence, and downtime trends across every asset in your fleet.
                </p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-4 md:flex-row md:items-center">
              <div className="inline-flex items-center gap-2 rounded-3xl border border-white/10 bg-white/10 px-4 py-3 text-xs font-medium text-slate-200 shadow-inner shadow-slate-900/30">
                <span className="text-base font-semibold text-white">{summary.totalEntries}</span>
                records in range
              </div>
              <TimeRangeSelector value={range} onChange={setRange} />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            {[
              { label: 'Tracked Equipment', value: summary.equipmentCount, description: 'Unique assets reporting telemetry', gradient: 'from-sky-400/90 to-blue-500/70' },
              { label: 'Downtime Alerts', value: summary.downtimeEvents, description: 'Events flagged for maintenance', gradient: 'from-amber-400/90 to-orange-500/70' },
              { label: 'Range Window', value: range.label, description: 'Selected monitoring interval', gradient: 'from-emerald-400/90 to-teal-500/70' },
            ].map(({ label, value, description, gradient }) => (
              <div
                key={label}
                className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/10 p-5 transition hover:border-white/30 hover:bg-white/20 dark:border-white/5 dark:bg-white/5"
              >
                <div className={`absolute -right-12 -top-12 h-32 w-32 rounded-full bg-gradient-to-br opacity-80 blur-2xl transition group-hover:opacity-100 ${gradient}`} />
                <div className="relative z-10 flex flex-col gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-200">{label}</span>
                  <span className="text-3xl font-semibold text-white">{value}</span>
                  <span className="text-xs text-slate-300">{description}</span>
                </div>
              </div>
            ))}
          </div>
        </header>

        {/* CONTENT (height = viewport - header - gap). No page scroll; inner panels may scroll if needed */}
        <div
          className={`grid min-h-0 flex-1 gap-5 ${compactLayout ? 'auto-rows-[minmax(280px,auto)] overflow-y-auto pr-1' : 'overflow-hidden'}`}
          style={compactLayout ? undefined : {
            height: contentHeight,
            gridTemplateRows: `repeat(2, ${rowHeight}px)`,
          }}
        >
          {/* Row 1 */}
          <div className={`grid min-h-0 gap-5 ${compactLayout ? 'grid-cols-1' : 'lg:auto-rows-[unset] lg:grid-cols-2'}`}>
            <section className={`${panelClasses} h-full`}>
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-white sm:text-lg">Feed to Plant</h2>
                <span className="text-[11px] font-medium text-slate-300 sm:text-xs">Smoothed telemetry</span>
              </div>
              <div className="mt-4 flex min-h-0 flex-1">
                <FeedToPlantChart range={range} />
              </div>
            </section>

            <section className={`${panelClasses} h-full`}>
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-white sm:text-lg">Downtime</h2>
                <span className="text-[11px] font-medium text-slate-300 sm:text-xs">Last 24 hours</span>
              </div>
              {/* KEY: give the inner wrapper real height and stretch children */}
              <div className="mt-4 flex min-h-0 h-full items-stretch">
                {/* Donut left / metrics right; stays inside height */}
                <DowntimeChart />
              </div>
            </section>
          </div>

          {/* Row 2 */}
          <div className={`grid min-h-0 gap-5 ${compactLayout ? 'grid-cols-1' : 'lg:auto-rows-[unset] lg:grid-cols-2'}`}>
            <section className={`${panelClasses} h-full`}>
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-white sm:text-lg">Inspection History</h2>
                <span className="text-[11px] font-medium text-slate-300 sm:text-xs">Latest submissions</span>
              </div>
              {/* Table scrolls inside panel if needed */}
              <div className="mt-4 flex min-h-0 flex-1 overflow-hidden rounded-2xl border border-white/10 bg-slate-950/40">
                <div className="min-h-0 flex-1 overflow-auto">
                  <InspectionHistoryTable data={data} />
                </div>
              </div>
            </section>

            <section className={`${panelClasses} h-full`}>
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-white sm:text-lg">Equipment Status</h2>
                <span className="text-[11px] font-medium text-slate-300 sm:text-xs">Live log</span>
              </div>
              <div className="mt-4 flex min-h-0 flex-1 overflow-hidden rounded-2xl border border-white/10 bg-slate-950/40">
                <div className="min-h-0 flex-1 overflow-auto">
                  <EquipmentStatusTable data={data} />
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
